import { buildExcludedTruckRefreshWarningsFingerprint } from "./protectedOrderAutoRefresh.js";

export const DISPATCH_PLANNING_LIVE_SYNC_POLL_INTERVAL_MS = 2000;
export const DISPATCH_PLANNING_LIVE_SYNC_JITTER_MAX_MS = 500;
export const DISPATCH_PLANNING_LIVE_SYNC_LEGACY_POLL_INTERVAL_MS = 15000;
export const DISPATCH_PLANNING_LIVE_SYNC_BACKOFF_MAX_MS = 30000;

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeDispatchPlanningRuntimeRevision(value) {
  return normalizeInlineText(value);
}

export function isDispatchPlanningRuntimeRevisionKnown({
  loadedRevision = "",
  locallySynchronizedRevision = "",
  requestedRevision = "",
} = {}) {
  const normalizedRequestedRevision = normalizeDispatchPlanningRuntimeRevision(requestedRevision);
  if (!normalizedRequestedRevision) {
    return false;
  }

  return normalizedRequestedRevision === normalizeDispatchPlanningRuntimeRevision(loadedRevision)
    || normalizedRequestedRevision === normalizeDispatchPlanningRuntimeRevision(
      locallySynchronizedRevision,
    );
}

export function buildDispatchPlanningRuntimeRevisionUrl(
  dispatchDate = "",
  dispatchPeriod = "day",
) {
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  if (!safeDispatchDate) {
    return "";
  }

  const params = new URLSearchParams({
    dispatch_date: safeDispatchDate,
    dispatch_period: normalizeInlineText(dispatchPeriod).toLowerCase() === "evening"
      ? "evening"
      : "day",
  });
  return `/api/planning/runtime-revision?${params.toString()}`;
}

export function resolveDispatchPlanningLiveSyncDelay({
  failureCount = 0,
  legacy = false,
  randomValue = Math.random(),
} = {}) {
  const normalizedRandomValue = Number.isFinite(randomValue)
    ? Math.min(Math.max(randomValue, 0), 1)
    : 0;
  const jitterMs = Math.round(
    normalizedRandomValue * DISPATCH_PLANNING_LIVE_SYNC_JITTER_MAX_MS,
  );
  if (legacy) {
    return DISPATCH_PLANNING_LIVE_SYNC_LEGACY_POLL_INTERVAL_MS + jitterMs;
  }

  const normalizedFailureCount = Math.max(Math.trunc(Number(failureCount) || 0), 0);
  const backoffMs = normalizedFailureCount > 0
    ? Math.min(
      DISPATCH_PLANNING_LIVE_SYNC_POLL_INTERVAL_MS * (2 ** normalizedFailureCount),
      DISPATCH_PLANNING_LIVE_SYNC_BACKOFF_MAX_MS,
    )
    : DISPATCH_PLANNING_LIVE_SYNC_POLL_INTERVAL_MS;
  return backoffMs + jitterMs;
}

export function resolveDispatchPlanningCompactProbeDecision({
  compactStatus = 0,
  legacy = false,
} = {}) {
  const compactEndpointMissing = Number(compactStatus) === 404;
  return {
    legacy: compactEndpointMissing,
    probeLegacyResults: Boolean(legacy) && compactEndpointMissing,
  };
}

export function startDispatchPlanningDeferredRevisionWait({
  expectedRevision = "",
  loadedRevision = "",
} = {}) {
  return {
    baselineRevision: normalizeDispatchPlanningRuntimeRevision(expectedRevision)
      || normalizeDispatchPlanningRuntimeRevision(loadedRevision),
    targetRevision: "",
  };
}

export function resolveDispatchPlanningDeferredRevisionProbe(
  waitState,
  remoteRevision = "",
) {
  const normalizedRemoteRevision = normalizeDispatchPlanningRuntimeRevision(remoteRevision);
  if (!waitState) {
    return {
      shouldRevalidate: Boolean(normalizedRemoteRevision),
      waitState: null,
    };
  }

  const baselineRevision = normalizeDispatchPlanningRuntimeRevision(
    waitState?.baselineRevision,
  );
  const targetRevision = normalizeDispatchPlanningRuntimeRevision(
    waitState?.targetRevision,
  );
  if (
    !normalizedRemoteRevision
    || normalizedRemoteRevision === targetRevision
    || (!targetRevision && normalizedRemoteRevision === baselineRevision)
  ) {
    return {
      shouldRevalidate: false,
      waitState: {
        baselineRevision,
        targetRevision,
      },
    };
  }

  return {
    shouldRevalidate: true,
    waitState: {
      baselineRevision,
      targetRevision: normalizedRemoteRevision,
    },
  };
}

export function completeDispatchPlanningDeferredRevisionWait(
  waitState,
  loadedRevision = "",
) {
  if (!waitState) {
    return null;
  }
  const targetRevision = normalizeDispatchPlanningRuntimeRevision(
    waitState?.targetRevision,
  );
  if (
    targetRevision
    && targetRevision === normalizeDispatchPlanningRuntimeRevision(loadedRevision)
  ) {
    return null;
  }
  return waitState;
}

function normalizeRouteSourceFingerprintValue(value) {
  const source = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (source === "estimate") {
    return "haversineestimate";
  }
  if (source === "geometry") {
    return "geometrymeasured";
  }
  return source;
}

function buildDispatchRouteSourcesFingerprint(resultsData) {
  const assignments = Array.isArray(resultsData?.assignments) ? resultsData.assignments : [];
  const routeSourceRecords = [];

  assignments.forEach((assignment, assignmentIndex) => {
    const truckId = typeof assignment?.truck_id === "string" && assignment.truck_id.trim()
      ? assignment.truck_id.trim()
      : typeof assignment?.truck_name === "string" && assignment.truck_name.trim()
        ? assignment.truck_name.trim()
        : `assignment-${assignmentIndex}`;
    const trips = Array.isArray(assignment?.trips) ? assignment.trips : [];

    trips.forEach((trip, tripIndex) => {
      const tripId = typeof trip?.trip_id === "string" && trip.trip_id.trim()
        ? trip.trip_id.trim()
        : `trip-${tripIndex}`;
      const route = trip?.route && typeof trip.route === "object" ? trip.route : {};
      const segmentSources = Array.isArray(route.segment_distances)
        ? route.segment_distances.map((segment) => normalizeRouteSourceFingerprintValue(segment?.source))
        : [];

      routeSourceRecords.push(JSON.stringify([
        truckId,
        tripId,
        normalizeRouteSourceFingerprintValue(route.source),
        normalizeRouteSourceFingerprintValue(route.actual_distance_source),
        normalizeRouteSourceFingerprintValue(route.estimated_distance_source),
        segmentSources,
      ]));
    });
  });

  return JSON.stringify(routeSourceRecords.sort());
}

function buildSourceDataStateFingerprint(resultsData) {
  const sourceDataState = resultsData?.source_data_state;
  if (!sourceDataState || typeof sourceDataState !== "object" || Array.isArray(sourceDataState)) {
    return "";
  }

  const normalizeList = (value) => (Array.isArray(value) ? value : [])
    .map((item) => (typeof item === "string" ? item.trim().toLowerCase() : ""))
    .filter(Boolean)
    .sort();
  const normalizeText = (value) => (typeof value === "string" ? value.trim() : "");

  return JSON.stringify({
    changeDomains: normalizeList(sourceDataState.change_domains),
    dispatchDates: normalizeList(sourceDataState.dispatch_dates),
    reason: normalizeText(sourceDataState.reason).toLowerCase(),
    reasons: normalizeList(sourceDataState.reasons),
    sourceSignature: normalizeText(sourceDataState.source_signature),
    stale: sourceDataState.stale === true,
    status: normalizeText(sourceDataState.status).toLowerCase(),
    updatedAt: normalizeText(sourceDataState.updated_at),
  });
}

export function buildDispatchPlanningLiveSyncFingerprint(resultsData) {
  if (!resultsData || typeof resultsData !== "object") {
    return "";
  }

  const planningSummary = resultsData.planning_summary && typeof resultsData.planning_summary === "object"
    ? resultsData.planning_summary
    : {};
  const stateUpdatedAt = typeof planningSummary.state_updated_at === "string" ? planningSummary.state_updated_at.trim() : "";
  const updatedAt = stateUpdatedAt || (typeof planningSummary.updated_at === "string" ? planningSummary.updated_at.trim() : "");
  const deliveryConfirmedCount = Number(planningSummary.delivery_confirmed_count) || 0;
  const confirmedCount = Number(planningSummary.confirmed_count) || 0;
  const generatedAt = typeof resultsData.generated_at === "string" ? resultsData.generated_at.trim() : "";
  const routeSourcesFingerprint = buildDispatchRouteSourcesFingerprint(resultsData);
  const warningsFingerprint = buildExcludedTruckRefreshWarningsFingerprint(resultsData);
  const sourceDataStateFingerprint = buildSourceDataStateFingerprint(resultsData);

  return `${generatedAt}::${updatedAt}::${deliveryConfirmedCount}::${confirmedCount}::${routeSourcesFingerprint}::${warningsFingerprint}::${sourceDataStateFingerprint}`;
}
